import { Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import Swal from 'sweetalert2';
import { AuthenticationService, UserService } from '../_services';
import { TranslateService } from '@ngx-translate/core';
import { IndexedDBService } from '../_services/indexdDb.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  dictUrlsActivities: { [key: string]: string } = {
    uploadDocs: '/upload',
    classifyReviewDocs: '/processamentos',
    admin: '/admin'
  };
  
  private useCheckUser = true;

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    public translate: TranslateService,
    private indexedDbService: IndexedDBService
  ) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const currentUser = this.authenticationService.currentUserValue;

    // Valida o token de forma assíncrona
    return this.authenticationService.validateToken().pipe(map((data) => {
        // console.log(data.message);
        this.useCheckUser = true;
        // Token válido, verifica permissões
        return this.checkUser(currentUser, state);
      }),
      catchError(error => {
        console.log("canActivate error: ", error);
        
        // Token inválido, limpa dados e redireciona
        this.handleTokenError(state.url);
        return of(false);
      })
    );
  }

  private checkUser(currentUser: any, state: RouterStateSnapshot): boolean | UrlTree {
    // console.log('call checkUser');
    const token = localStorage.getItem("token");
    if(token) {
      // Usuário logado, verifica URLs disponíveis
      return this.avaliableUrls(state.url, currentUser);
    } else {
      // Não logado, redireciona para login
      return this.redirectToLogin(state.url);
    }
  }

  private avaliableUrls(stateUrl: string, user:any): boolean | UrlTree {
    // console.log('call avaliableUrls');
    // selectedRole/selectedEmpresa vivem em sessionStorage (isolado por aba, gravados
    // como JSON em login.component.ts/change-company.component.ts) - não em
    // localStorage, para uma aba não trocar o contexto de empresa/perfil de outra.
    const storedSelectedRole = sessionStorage.getItem("selectedRole");
    const selectedRole: any = storedSelectedRole ? JSON.parse(storedSelectedRole) : user.selectedRole;
    const storedSelectedEmpresa = sessionStorage.getItem("selectedEmpresa");
    const selectedEmpresa: any = storedSelectedEmpresa ? JSON.parse(storedSelectedEmpresa) : user.selectedEmpresa;
    const isAdmin: any = localStorage.getItem("isAdmin") || user.isAdmin;
    const masterAdmin: any = localStorage.getItem("masterAdmin") || user.masterAdmin;
    if(selectedRole) {
      let validRoute = false;
      let myActivity: { [key: string]: any } | null = null;
      
      // Procura a atividade correspondente à empresa selecionada
      if (selectedRole.activities && Array.isArray(selectedRole.activities)) {
        selectedRole.activities.forEach((activity: any) => {
          if(selectedEmpresa && activity.empresa_id === selectedEmpresa._id) {
            myActivity = activity;
          }
        });
      }

      if(myActivity) {
        const keysActivities = Object.keys(myActivity).filter((key: string) => myActivity![key] === true);
        
        if(keysActivities.length > 0) {
          keysActivities.every((key: string) => {
            // Verifica se a URL atual está nas rotas permitidas
            if (this.isRouteAllowed(stateUrl, key)) {
              validRoute = true;
              return false; // Para o loop
            }
            return true; // Continua o loop
          });
        } else {
          validRoute = true;
        }
      } else {
        validRoute = true;
      }

      if(!validRoute) {
        return this.router.createUrlTree(['/forbidden']);
      }
      
      return validRoute;
    } else {
      // Verifica se é admin para rotas administrativas
      if (!(isAdmin || masterAdmin)) {
        if (stateUrl.includes('/admin/relatorio-extracao')) {
          // Permite acesso a relatórios de extração
          return true;
        } else if (stateUrl.includes('/admin')) {
          return this.router.createUrlTree(['/forbidden']);
        }
      }
      return true;
    }
  }

  private isRouteAllowed(stateUrl: string, key: string): boolean {
    // Rotas sempre permitidas
    const alwaysAllowedRoutes = [
      "dashboard", 
      "documentos", 
      "home", 
      "not-found", 
      "forbidden"
    ];

    // Verifica rotas sempre permitidas
    if(alwaysAllowedRoutes.some((route) => stateUrl.includes(route))) {
      return true;
    }

    // Verifica se a chave existe no dicionário e se a URL inclui a rota
    if(key in this.dictUrlsActivities) {
      return stateUrl.includes((this.dictUrlsActivities as any)?.[key]);
    }

    return false;
  }

  private redirectToLogin(returnUrl: string): UrlTree {
    if (!this.router.url.includes('/login')) {
      return this.router.createUrlTree(
        [this.authenticationService.currentLoginPathValue], 
        { queryParams: { returnUrl } }
      );
    }
    return this.router.createUrlTree(['/login']);
  }

  private handleTokenError(returnUrl: string): void {
    this.authenticationService.isLoginSubject.next(false);
    const lang = localStorage.getItem('lang') || 'pt';
    
    // Limpa dados do IndexedDB
    this.indexedDbService.clearStore('userData').then(result => {
      console.log('IndexedDB cleared:', result);
    }).catch(error => {
      console.error('Error clearing IndexedDB:', error);
    });

    // Limpa localStorage mas preserva idioma
    localStorage.clear();
    if(lang) {
      localStorage.setItem('lang', lang);
    }

    // Atualiza subject do usuário atual
    this.authenticationService.currentUserSubject.next(null);

    // Navega para login se não estiver já na página de login
    if (!this.router.url.includes('/login')) {
      this.router.navigate(
        [this.authenticationService.currentLoginPathValue], 
        { queryParams: { returnUrl } }
      );
    }
  }
}
